import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import classNames from "classnames";
import Cookies from "js-cookie";
import MarksRegistryService, {
  MarkCardResponse,
  MarkMovementItem,
} from "../../common/api/marksRegistryApi";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import Loader from "../../shared/Ui/Loader/Loader";
import StatusBadge from "../../shared/Ui/StatusBadge/StatusBadge";
import CopyButton from "../../shared/Ui/CopyButton/CopyButton";
import { getMarkStatusType } from "../../shared/documentUtils/dictionary";
import { normalizedLocale } from "../../common/libs/normalizedLocale";
import { product_groups } from "../../shared/reference/product_groups";
import { personalization_groups } from "../../shared/reference/personalization_groups";
import MarkMovementTab from "./MarkMovementTab";
import style from "./MarkingCodes.module.css";

type TabId = "general" | "movement";

type PersonalField = {
  labelRu?: string;
  labelKz?: string;
  value?: string;
  valuePath?: string;
  valueRu?: string;
  valueKz?: string;
};

function formatKmType(kmType: string | undefined, t: (k: string) => string) {
  if (kmType === "GROUP") return t("marking_codes.km_type_group");
  if (kmType === "CONSUMER") return t("marking_codes.km_type_consumer");
  return kmType || "—";
}

function orgLabel(
  org: { nameRu?: string; nameKk?: string; iinBin?: string } | null | undefined,
  locale: string,
) {
  if (!org) return "—";
  const name = locale === "kk" ? org.nameKk || org.nameRu : org.nameRu || org.nameKk;
  if (name && org.iinBin) return `${name} (${org.iinBin})`;
  return name || org.iinBin || "—";
}

function readByPath(source: any, path: string): unknown {
  const parts = path.split(".");
  let acc: any = source;
  for (const part of parts) {
    if (acc == null) return undefined;
    const index = Number(part);
    acc =
      Number.isInteger(index) && String(index) === part
        ? acc?.[index]
        : acc?.[part];
  }
  return acc;
}

function inferProductGroupCode(card: MarkCardResponse): string | null {
  if (card.woodInfo) return "002";
  if (card.gasInfo) return "003";
  if (card.derivativeInfo) return "004";
  if (card.weight != null || card.size != null) return "001";
  const cookieGroup = Cookies.get("activeGroup");
  if (cookieGroup && ["001", "002", "003", "004"].includes(cookieGroup)) {
    return cookieGroup;
  }
  return null;
}

function getPersonalCategory(groupCode: string | null) {
  if (!groupCode) return null;
  const group = product_groups.find((pg: any) => pg.code === groupCode) as any;
  return (
    group?.categories?.find(
      (c: any) => c.key === "personal" || c.key === "personalCharacteristics",
    ) || null
  );
}

function jewelryPersonalFields(): PersonalField[] {
  return [
    {
      labelRu: "Вес, г",
      labelKz: "Салмақ, г",
      value: "weight",
    },
    {
      labelRu: "Размер, мм",
      labelKz: "Өлшем, мм",
      value: "size",
    },
  ];
}

function resolveOptionLabel(
  fieldKey: string,
  raw: unknown,
  groupCode: string | null,
  locale: string,
) {
  if (raw == null || raw === "") return null;
  if (!groupCode) return String(raw);
  const group = personalization_groups.find((pg: any) => pg.code === groupCode) as any;
  const fields = group?.categories?.find((c: any) => c.key === "personalization")?.fields || [];
  const field = fields.find((f: any) => f.value === fieldKey);
  const option = field?.options?.find(
    (o: any) => String(o.value) === String(raw),
  );
  return (locale === "kk" && option?.labelKz) || option?.label || String(raw);
}

function readPersonalFieldValue(
  card: MarkCardResponse,
  field: PersonalField,
  groupCode: string | null,
  locale: string,
): string {
  let raw: unknown;
  if (field.valuePath) {
    raw = readByPath(card, field.valuePath);
  } else if (field.value) {
    raw = (card as any)[field.value];
  }

  if (raw == null || raw === "") return "—";

  const leafKey = field.valuePath
    ? field.valuePath.split(".").slice(-1)[0]
    : field.value;
  if (leafKey && (field.valuePath?.includes("woodInfo") || leafKey === "woodType" || leafKey === "woodSpecies" || leafKey === "sort" || leafKey === "sourceCode")) {
    const mapped = resolveOptionLabel(leafKey, raw, groupCode, locale);
    if (mapped) return mapped;
  }

  return String(raw);
}

function collectPersonalFields(
  card: MarkCardResponse,
  groupCode: string | null,
): { titleRu: string; titleKz: string; fields: PersonalField[] } | null {
  const category = getPersonalCategory(groupCode);
  if (category?.fields?.length) {
    return {
      titleRu: category.labelRu || "Персональные данные",
      titleKz: category.labelKz || "Жеке деректер",
      fields: category.fields,
    };
  }
  if (groupCode === "001" || card.weight != null || card.size != null) {
    return {
      titleRu: "Персональные данные",
      titleKz: "Жеке деректер",
      fields: jewelryPersonalFields(),
    };
  }
  return null;
}

export default function MarkingCodeCard() {
  const { markId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = normalizedLocale(i18n.language);

  const [tab, setTab] = useState<TabId>("general");
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState<MarkCardResponse | null>(null);
  const [dmUrl, setDmUrl] = useState<string | null>(null);
  const [movement, setMovement] = useState<MarkMovementItem[]>([]);
  const [movementLoading, setMovementLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!markId) return;
    let revoked: string | null = null;
    setLoading(true);
    setError(null);

    Promise.all([
      MarksRegistryService.getMarkCard(markId),
      MarksRegistryService.getMarkDatamatrix(markId).catch(() => null),
    ])
      .then(([cardRes, blob]) => {
        setCard(cardRes);
        if (blob) {
          const url = URL.createObjectURL(blob);
          revoked = url;
          setDmUrl(url);
        }
      })
      .catch((e: any) => {
        setError(
          e?.response?.data?.message ||
            e?.response?.data?.errorMessage ||
            t("marking_codes.load_error"),
        );
        setCard(null);
      })
      .finally(() => setLoading(false));

    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [markId, t]);

  useEffect(() => {
    if (!markId || tab !== "movement") return;
    setMovementLoading(true);
    MarksRegistryService.getMarkMovement(markId)
      .then((data) => setMovement(data))
      .catch(() => setMovement([]))
      .finally(() => setMovementLoading(false));
  }, [markId, tab]);

  if (loading) return <Loader />;

  if (error || !card) {
    return (
      <div className="flex flex-1 flex-col min-w-0 p-4">
        <EmptyListPlaceholderContent
          title={t("marking_codes.load_error")}
          description={error || ""}
        />
        <button
          type="button"
          className="mt-4 self-start bg-bg text-gray-600 font-bold py-2 px-4 rounded"
          onClick={() => navigate("/marking-codes")}
        >
          {t("mainButtons.back")}
        </button>
      </div>
    );
  }

  const statusInfo = getMarkStatusType(card.status || "", t);
  const nkt = card.nktCard;
  const groupCode = inferProductGroupCode(card);
  const personalBlock = collectPersonalFields(card, groupCode);
  const personalTitle =
    locale === "kk"
      ? personalBlock?.titleKz || personalBlock?.titleRu
      : personalBlock?.titleRu;

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className={style.breadcrumb}>
        <span
          className={style.breadcrumbLink}
          onClick={() => navigate("/marking-codes")}
        >
          {t("marking_codes.title_short")}
        </span>
        {" > "}
        <span>
          {t("marking_codes.code_prefix")} {card.markCode}
        </span>
      </div>

      <div className={style.tabs}>
        {(
          [
            ["general", t("marking_codes.tab_general")],
            ["movement", t("marking_codes.tab_movement")],
          ] as [TabId, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={classNames(style.tab, tab === id && style.tabActive)}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={style.cardShell}>
        {tab === "general" && (
          <div className={style.generalGrid}>
            <div>
              <div className={style.sectionTitle}>
                {t("marking_codes.mark_info")}
              </div>
              <div className="flex gap-4 mb-4">
                {dmUrl ? (
                  <img
                    src={dmUrl}
                    alt="DataMatrix"
                    className={style.dmImage}
                  />
                ) : (
                  <div className={style.dmImage} />
                )}
                <div>
                  <div className={style.fieldLabel}>{t("marking_codes.col_code")}</div>
                  <div className={style.fieldValue}>{card.markCode}</div>
                  <div className={style.fieldLabel}>{t("marking_codes.col_status")}</div>
                  <div className={style.fieldValue}>
                    <StatusBadge type={statusInfo.type}>
                      {statusInfo.label}
                    </StatusBadge>
                  </div>
                </div>
              </div>

              {(card.statusHistory || []).map((h, i) => (
                <div key={`${h.status}-${i}`}>
                  <div className={style.fieldLabel}>
                    {getMarkStatusType(h.status || "", t).label}
                  </div>
                  <div className={style.fieldValue}>{h.assignedAt || "—"}</div>
                </div>
              ))}

              <div className={style.fieldLabel}>{t("marking_codes.issuer")}</div>
              <div className={style.fieldValue}>
                {orgLabel(card.issuer, locale)}
              </div>
              <div className={style.fieldLabel}>{t("marking_codes.owner")}</div>
              <div className={style.fieldValue}>
                {orgLabel(card.owner, locale)}
              </div>
            </div>

            <div>
              <div className={style.sectionTitle}>
                {t("marking_codes.product_info")}
              </div>
              <div className={style.fieldLabel}>
                {t("marking_codes.col_product_type_name")}
              </div>
              <div className={style.fieldValue}>
                {card.productTypeName || "—"}
              </div>
              <div className={style.fieldLabel}>NTIN</div>
              <div className={`${style.fieldValue} inline-flex items-center gap-1`}>
                <span>{card.ntin || "—"}</span>
                <CopyButton value={card.ntin} />
              </div>
              <div className={style.fieldLabel}>
                {t("marking_codes.col_ntin_name")}
              </div>
              <div className={style.fieldValue}>
                {(locale === "kk"
                  ? nkt?.nameKz || nkt?.nameKk || nkt?.nameRu
                  : nkt?.nameRu || nkt?.nameKz || nkt?.nameKk) || "—"}
              </div>
              <div className={style.fieldLabel}>
                {t("marking_codes.col_km_type")}
              </div>
              <div className={style.fieldValue}>
                {formatKmType(card.kmType, t)}
              </div>
              {(card.status === "ISSUED" || card.status === "INFLICTED") && (
                <>
                  <div className={style.fieldLabel}>
                    {t("marking_codes.order_number")}
                  </div>
                  <div className={style.fieldValue}>
                    {card.orderNumber || "—"}
                  </div>
                </>
              )}
              {personalBlock?.fields?.length ? (
                <>
                  <div className={`${style.sectionTitle} mt-4`}>
                    {personalTitle}
                  </div>
                  {personalBlock.fields.map((field) => {
                    const label =
                      locale === "kk"
                        ? field.labelKz || field.labelRu
                        : field.labelRu || field.labelKz;
                    const key = field.valuePath || field.value || label || "field";
                    return (
                      <div key={key}>
                        <div className={style.fieldLabel}>{label}</div>
                        <div className={style.fieldValue}>
                          {readPersonalFieldValue(card, field, groupCode, locale)}
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : null}
              {card.kmType === "GROUP" && (
                <>
                  <div className={style.fieldLabel}>
                    {t("marking_codes.child_count")}
                  </div>
                  <div className={style.fieldValue}>
                    {card.childMarksCount ?? "—"}
                  </div>
                </>
              )}
              {nkt?.brand && (
                <>
                  <div className={style.fieldLabel}>{t("marking_codes.brand")}</div>
                  <div className={style.fieldValue}>{nkt.brand}</div>
                </>
              )}
            </div>
          </div>
        )}

        {tab === "movement" && (
          <MarkMovementTab
            items={movement}
            loading={movementLoading}
            orderNumber={card.orderNumber}
          />
        )}
      </div>
    </div>
  );
}
